import { useCallback, useMemo } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useBidsStore } from '@/stores/bids-store'
import { hasCompletedRun, useExtractionStore } from '@/stores/extraction-store'
import { useReconciliationStore } from '@/stores/reconciliation-store'
import { useTenderOverviewStore } from '@/stores/tender-overview-store'
import { TENDER_OVERVIEW_SECTION_KEYS } from '@/types/tender-overview'
import type { TenderOverview } from '@/types/tender-overview'
import type {
  ExtractedComplianceItem,
  ExtractedCriterion,
  ExtractedDate,
  ExtractedRiskItem,
} from '@/types/extraction'
import { EmptyOverviewCard } from '@/components/overview/EmptyOverviewCard'
import { HarvestOutcomeAffordances } from '@/components/overview/HarvestOutcomeAffordances'
import { NextActionsBlock } from '@/components/overview/NextActionsBlock'
import { SubmissionDeadlineCard } from '@/components/overview/SubmissionDeadlineCard'
import { KeyDatesTimeline } from '@/components/overview/KeyDatesTimeline'
import { EvalCriteriaSummary } from '@/components/overview/EvalCriteriaSummary'
import { ComplianceSnapshot } from '@/components/overview/ComplianceSnapshot'
import { RiskSnapshot } from '@/components/overview/RiskSnapshot'
import { TenderSummaryCard } from '@/components/overview/TenderSummaryCard'
import { ResponseProgress } from '@/components/overview/ResponseProgress'
import { PackSnapshot } from '@/components/overview/PackSnapshot'
import { RecentActivity } from '@/components/overview/RecentActivity'
import { StatTileVariantContext } from '@/components/overview/OverviewStatTile'
import { OverviewStrategySummary } from '@/components/strategy/OverviewStrategySummary'
import { useBidReadOnly } from '@/hooks/useViewOnly'

interface BidOverviewSectionProps {
  bidId: string
}

function isExtracting(state: { state: string; isRecovering?: boolean }) {
  // Treat `isRecovering` as in-progress too — it covers the post-refresh
  // rehydration path where the run is still going in the background but the
  // streaming connection has been replaced by REST polling.
  return state.state === 'extracting' || state.isRecovering === true
}

function getSubmissionDeadline(dates: ExtractedDate[]): ExtractedDate | null {
  const sorted = [...dates].sort((a, b) => {
    if (!a.dateIso) return 1
    if (!b.dateIso) return -1
    return new Date(a.dateIso).getTime() - new Date(b.dateIso).getTime()
  })
  return sorted.find((item) => item.isSubmissionDeadline) ?? sorted[0] ?? null
}

// Citation markers, single or grouped: `[1]`, `[1, 3]`, plus the space before.
const CITATION_MARKER_REGEX = /\s*\[\d+(?:\s*,\s*\d+)*\]/g

/**
 * A short summary drawn from the Analysis tab's Tender Summary, for bids whose
 * stored `tenderSummary` was never written (that field is only filled by the
 * Analyse Bid dialog). Takes the opening block of the first addressed section
 * — normally Scope of works — with citation markers removed, since the card
 * has no sources to resolve them against.
 */
function getOverviewExcerpt(overview: TenderOverview): string | null {
  for (const key of TENDER_OVERVIEW_SECTION_KEYS) {
    const section = overview.sections.find((s) => s.key === key)
    if (!section || section.notAddressed) continue
    const firstBlock = section.contentMd.trim().split(/\n\s*\n/)[0] ?? ''
    const excerpt = firstBlock.replace(CITATION_MARKER_REGEX, '').trim()
    if (excerpt) return excerpt
  }
  return null
}

export function BidOverviewSection({ bidId }: BidOverviewSectionProps) {
  const bid = useBidsStore((state) => state.selectedBid)
  // An org viewer, or a member who is not on the bid: the deadline is theirs to read only.
  const { isReadOnly } = useBidReadOnly(bidId)
  const updateBid = useBidsStore((state) => state.updateBid)
  const currentBidId = useExtractionStore((state) => state.currentBidId)
  const categories = useExtractionStore((state) => state.categories)
  const storedOverview = useTenderOverviewStore((state) => state.overview)
  // The store is global; never show another bid's overview under this one.
  const tenderOverview = storedOverview?.bidId === bidId ? storedOverview : null
  const overviewExcerpt = useMemo(
    () => (tenderOverview ? getOverviewExcerpt(tenderOverview) : null),
    [tenderOverview]
  )
  const summaryText = bid?.tenderSummary || overviewExcerpt

  const evaluationCriteria = categories.evaluation_criteria.items as ExtractedCriterion[]
  const complianceItems = categories.compliance_matrix.items as ExtractedComplianceItem[]
  const keyDates = categories.key_dates.items as ExtractedDate[]
  const riskItems = categories.risk_register.items as ExtractedRiskItem[]

  // Applying the extracted closing date to the bid record (F39). Offered on the
  // deadline card because that is where the contradiction with the metadata
  // strip is visible. Undo is offered only when there was a previous deadline
  // to restore: undoing back to "no deadline" would be recorded as a person
  // clearing it (deadline_source 'user_cleared'), and key-dates extraction
  // would then never fill it again.
  // The extracted value is a date, so the server stores it date-only; Undo
  // restores the previous deadline's closing time along with it.
  const handleSetBidDeadline = useCallback(
    (dateIso: string) => {
      const previous = bid?.deadline
      const previousHasTime = bid?.deadlineHasTime === true
      void (async () => {
        try {
          await updateBid({ id: bidId, deadline: dateIso })
          toast.success(
            'Bid deadline updated',
            previous
              ? {
                  action: {
                    label: 'Undo',
                    onClick: () =>
                      void updateBid({ id: bidId, deadline: previous, deadlineHasTime: previousHasTime }),
                  },
                }
              : undefined
          )
        } catch {
          toast.error('Failed to update bid deadline')
        }
      })()
    },
    [bid?.deadline, bid?.deadlineHasTime, bidId, updateBid]
  )

  // "No analysis yet" is a claim that nothing has ever run, so a category that
  // ran and found nothing must not count towards it — otherwise a bid where
  // every extraction completed with zero items renders the first-run hero and
  // invites a re-run of billed analysis. `hasCompletedRun` (the named selector,
  // not a raw `lastExtractedAt` check) keeps this in step with the other
  // surfaces; unloaded categories still have `lastExtractedAt: null`, so the
  // pre-scope loading gate below is unaffected.
  const allEmpty = useMemo(() => {
    return Object.values(categories).every(
      (category) => category.items.length === 0 && category.state === 'empty' && !hasCompletedRun(category)
    )
  }, [categories])

  const submissionDeadline = useMemo(() => getSubmissionDeadline(keyDates), [keyDates])

  // Surface a pending addendum-proposed deadline change from the page-hydrated
  // reconciliation runs. Highlights are server-gated to review-status runs with
  // pending proposals; runs are ordered newest-first, so the most recent pending
  // proposal wins. Suppress the warnstrip unless the proposal names a concrete
  // date (proposedDateIso is a non-null string) — no date, no warnstrip.
  const runs = useReconciliationStore((state) => state.runsByBid[bidId])
  const pendingDeadlineChange = useMemo(() => {
    const run = (runs ?? []).find((r) => typeof r.highlights?.deadlineChange?.proposedDateIso === 'string')
    const change = run?.highlights?.deadlineChange
    if (!run || !change || typeof change.proposedDateIso !== 'string') return null
    return { label: change.label, proposedDateIso: change.proposedDateIso, runId: run.id }
  }, [runs])

  if (currentBidId !== bidId && allEmpty) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading analysis overview...</span>
      </div>
    )
  }

  if (allEmpty && !bid?.tenderSummary && !tenderOverview) {
    return (
      <div className="space-y-6">
        {/* A submitted bid can still need its result recorded before anyone analyses it. */}
        <HarvestOutcomeAffordances bidId={bidId} />
        <div className="px-6 py-12 text-center">
          <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="text-base font-medium">No analysis yet</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Analyse the bid to generate key dates, evaluation criteria, compliance items, and a tender summary.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Command center: signals first. A compact, hairline-divided bar of stat
          cells so the numbers a bid manager needs in 5 seconds sit in the first
          viewport, each linking to the full data on Analysis / Strategy / Responses. */}
      <StatTileVariantContext.Provider value="bar">
        <div className="flex overflow-x-auto rounded-xl border border-border bg-card shadow-sm divide-x divide-border [&>*]:min-w-[176px] [&>*]:flex-1">
          <EvalCriteriaSummary
            bidId={bidId}
            criteria={evaluationCriteria}
            categoryState={categories.evaluation_criteria}
            loading={isExtracting(categories.evaluation_criteria)}
          />
          <ComplianceSnapshot
            bidId={bidId}
            items={complianceItems}
            categoryState={categories.compliance_matrix}
            loading={isExtracting(categories.compliance_matrix)}
          />
          <KeyDatesTimeline
            bidId={bidId}
            dates={keyDates}
            categoryState={categories.key_dates}
            loading={isExtracting(categories.key_dates)}
          />
          <RiskSnapshot
            bidId={bidId}
            items={riskItems}
            categoryState={categories.risk_register}
            loading={isExtracting(categories.risk_register)}
          />
          {/* Strategy emits 0–2 tiles (Go/No-Go + Win Themes); Response Progress
              always emits one — its empty state is the first-run nudge. */}
          <OverviewStrategySummary bidId={bidId} />
          <ResponseProgress bidId={bidId} />
          <PackSnapshot bidId={bidId} />
        </div>
      </StatTileVariantContext.Provider>

      {/* "What's next" — 1–3 prioritized workflow nudges; renders nothing when clear. */}
      <NextActionsBlock bidId={bidId} />

      {/* Lazy capture: late harvest + outcome details (renders nothing when done). */}
      <HarvestOutcomeAffordances bidId={bidId} />

      {/* Context: the prose summary fills the width (no floating measure inside
          a half-empty card), with the submission deadline as a full-width card
          below — robust to any summary length, unlike a height-matched rail. */}
      {summaryText ? (
        <TenderSummaryCard
          summary={summaryText}
          fullSummaryTo={
            tenderOverview ? `/app/bids/${bidId}/qualify?view=tender_overview` : undefined
          }
        />
      ) : (
        <EmptyOverviewCard title="Summary" description="Not yet analysed." bidId={bidId} />
      )}

      {submissionDeadline && (
        <SubmissionDeadlineCard
          deadline={submissionDeadline}
          bidId={bidId}
          pendingChange={pendingDeadlineChange}
          bidDeadline={bid?.deadline ?? null}
          // Omitted for a read-only user: the card then renders read-only.
          onSetBidDeadline={isReadOnly ? undefined : handleSetBidDeadline}
        />
      )}

      <RecentActivity bidId={bidId} />
    </div>
  )
}
